'use client';

import {
  createContext,
  useContext,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';

import {
  type Locale,
  type TranslationInterface,
  defaultLocale,
  getTranslation,
  isRTL,
  localeNames,
} from '.';

// Context type
interface LocaleContextType {
  locale: Locale;
  t: TranslationInterface;
  isRTL: boolean;
  localeName: string;
}

// Create context with default values
const LocaleContext = createContext<LocaleContextType>({
  locale: defaultLocale,
  t: getTranslation(defaultLocale),
  isRTL: false,
  localeName: localeNames[defaultLocale],
});

// Provider props
interface LocaleProviderProps {
  children: ReactNode;
  locale: Locale;
}

// Provider component
export function LocaleProvider({ children, locale }: LocaleProviderProps) {
  const isRtl = isRTL(locale);

  // Update HTML attributes when locale changes (for client-side navigation)
  useEffect(() => {
    const html = document.documentElement;
    html.lang = locale;
    html.dir = isRtl ? 'rtl' : 'ltr';
  }, [locale, isRtl]);

  const value: LocaleContextType = {
    locale,
    t: getTranslation(locale),
    isRTL: isRtl,
    localeName: localeNames[locale],
  };

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

// Hook to use translations
export function useTranslation() {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LocaleProvider');
  }
  return context;
}

// Hook to use locale only
export function useLocale(): Locale {
  const { locale } = useTranslation();
  return locale;
}

// Utility hook for generating localized paths
export function useLocalePath() {
  const { locale } = useTranslation();

  const getLocalePath = useCallback(
    (path: string): string => {
      // English (default) has no prefix
      if (locale === defaultLocale) {
        return path;
      }
      // Other locales get prefixed
      return `/${locale}${path.startsWith('/') ? path : `/${path}`}`;
    },
    [locale],
  );

  return { getLocalePath };
}
